import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, type ReactElement } from "react";
import { useTopbar } from "@/components/chrome/use-topbar";
import { requireAdminRoute } from "@/lib/require-admin-route";

export const Route = createFileRoute("/_app/settings/")({
  beforeLoad: requireAdminRoute,
  component: SettingsHub,
});

interface Card {
  to:
    | "/settings/lenders"
    | "/settings/markets"
    | "/settings/icp"
    | "/settings/dynamo-terms"
    | "/settings/schedule"
    | "/settings/audit"
    | "/settings/users"
    | "/settings/api-usage"
    | "/settings/me";
  title: string;
  blurb: string;
  icon: ReactElement;
  strategic?: boolean;
}

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
} as const;

const CARDS: Card[] = [
  {
    to: "/settings/lenders",
    title: "Lenders",
    blurb:
      "Whitelist of competitor lenders that trigger Priority A re-underwriting. Any loan from these lenders gets re-underwritten regardless of other filters.",
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-7h6v7M9 9h.01M15 9h.01" />
      </svg>
    ),
  },
  {
    to: "/settings/markets",
    title: "Markets",
    blurb:
      "Geographic filter, loan size band, and loan type. Defines the haystack the daily scan searches for new originations.",
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    to: "/settings/icp",
    title: "ICP rules",
    blurb:
      "Pass/fail filters applied after a loan matches the haystack. Borrower must clear all six to enter the daily queue.",
    icon: (
      <svg {...ICON_PROPS}>
        <circle cx="12" cy="12" r="10" />
        <path d="M8 12l3 3 5-6" />
      </svg>
    ),
  },
  {
    to: "/settings/dynamo-terms",
    title: "Dynamo terms",
    blurb:
      "Where the dollar story comes from. Max LTV against ARV, rate, points, rehab budget rules, and interest reserve treatment.",
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    strategic: true,
  },
  {
    to: "/settings/schedule",
    title: "Schedule",
    blurb:
      "Daily run cadence, lookback window, and email recipients for the on-demand PDF workflow.",
    icon: (
      <svg {...ICON_PROPS}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
  },
  {
    to: "/settings/audit",
    title: "Audit log",
    blurb:
      "Read-only history of every config change. Filter by user or parameter group. Required for compliance review.",
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </svg>
    ),
  },
  {
    to: "/settings/users",
    title: "Users & roles",
    blurb:
      "Invite teammates, set admin vs. user access, manage signing eligibility, and upload profile photos.",
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    strategic: true,
  },
  {
    to: "/settings/api-usage",
    title: "API usage",
    blurb:
      "Radar (SFR & PLR) API call volume by month — totals, by-type breakdown, trends, and a per-run table. Counters reset on the 1st.",
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M3 3v18h18M7 14l4-4 3 3 5-6" />
      </svg>
    ),
  },
  {
    to: "/settings/me",
    title: "My profile",
    blurb:
      "Your display name, contact phone, contact email, and whether you can sign borrower mailers. Shown on PDFs and in the sidebar.",
    icon: (
      <svg {...ICON_PROPS}>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
      </svg>
    ),
  },
];

function SettingsHub() {
  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [{ label: "Operations" }, { label: "Settings" }],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  return (
    <section className="space-y-6">
      <div className="border-b border-rule pb-5">
        <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Settings</h1>
        <p className="text-sm text-muted">
          Tune the daily scan, ICP rules, and Dynamo loan parameters.
        </p>
      </div>

      <div className="flex items-baseline justify-between">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-ink">
          Parameter groups
        </h2>
        <span className="text-[11px] text-muted">7 groups · changes audited per-field</span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="flex flex-col gap-4 rounded-lg border border-rule bg-surface p-5 transition hover:border-rule-strong"
          >
            <div className="flex items-center gap-3">
              <span
                className={`grid h-[38px] w-[38px] place-items-center rounded-md ${
                  c.strategic ? "bg-ink text-tan-light" : "bg-paper-2 text-ink"
                }`}
              >
                {c.icon}
              </span>
              <span className="text-[14px] font-semibold text-ink">{c.title}</span>
            </div>
            <p className="text-[12px] leading-relaxed text-muted">{c.blurb}</p>
            <div className="mt-auto flex items-center justify-end gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-2">
              Edit
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
